import { useQuery } from '@tanstack/react-query';
import { AlertCircle, CheckCircle2, MessageSquare, PauseCircle } from 'lucide-react';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableSkeleton } from '@/components/Skeletons';

interface OverviewResponse {
  enabled: boolean;
  config: {
    subdomain: string | null;
    field_id: number | null;
    webhook_path: string | null;
  };
  stats: Record<string, { '24h': number; '7d': number }>;
  recent: Array<{
    id: string;
    kommo_lead_id: string;
    kommo_note_id: string;
    status: 'pending' | 'processing' | 'done' | 'skipped' | 'failed' | 'dead';
    last_error: string | null;
    duration_ms: number | null;
    received_at: string;
    processed_at: string | null;
  }>;
}

export function LastNote(): JSX.Element {
  const { data, isLoading } = useQuery({
    queryKey: ['last-note-overview'],
    queryFn: () => api.get<OverviewResponse>('/admin/last-note/overview'),
    refetchInterval: 5_000,
  });

  const stat = (key: string): { '24h': number; '7d': number } =>
    data?.stats[key] ?? { '24h': 0, '7d': 0 };
  const done = stat('done');
  const skipped = stat('skipped');
  const failed = stat('failed');
  const dead = stat('dead');

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Last Note</h1>
        <p className="text-sm text-muted-foreground">
          Запись последнего комментария Kommo в кастомное поле сделки.
          Обновляется каждые 5 секунд.
        </p>
      </header>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-base">
            <span>Конфигурация</span>
            {data && (
              <Badge variant={data.enabled ? 'success' : 'secondary'}>
                {data.enabled ? 'Активен' : 'Не сконфигурирован'}
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data && <div className="text-sm text-muted-foreground">Загрузка…</div>}
          {data && (
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <ConfigRow label="Kommo subdomain" value={data.config.subdomain ?? '—'} />
              <ConfigRow label="Custom field ID" value={data.config.field_id?.toString() ?? '—'} />
              <ConfigRow
                label="Webhook URL"
                value={data.config.webhook_path ?? '—'}
                mono
              />
              <ConfigRow
                label="AI-маркеры"
                value="[AI Summary], [Bridge AI Weekly] — игнорируем"
              />
            </dl>
          )}
        </CardContent>
      </Card>

      <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <StatCard icon={CheckCircle2} tone="success" label="Записано" stats={done} />
        <StatCard icon={PauseCircle} tone="muted" label="Пропущено" stats={skipped} />
        <StatCard icon={AlertCircle} tone="warning" label="Ошибки" stats={failed} />
        <StatCard icon={AlertCircle} tone="destructive" label="Dead" stats={dead} />
      </section>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <MessageSquare className="h-4 w-4" />
            Последние 50 событий
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <TableSkeleton
              columns={['ID', 'Lead', 'Note', 'Status', 'Error', 'ms', 'Received']}
              rows={5}
            />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-16">ID</TableHead>
                  <TableHead className="w-28">Lead</TableHead>
                  <TableHead className="w-28">Note</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead>Error / Reason</TableHead>
                  <TableHead className="w-20 text-right">ms</TableHead>
                  <TableHead className="w-40">Received</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.recent.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                      Событий пока нет.
                    </TableCell>
                  </TableRow>
                )}
                {data?.recent.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell className="font-mono text-xs text-muted-foreground">#{e.id}</TableCell>
                    <TableCell className="font-mono text-xs">{e.kommo_lead_id}</TableCell>
                    <TableCell className="font-mono text-xs">{e.kommo_note_id}</TableCell>
                    <TableCell>
                      <StatusBadge status={e.status} />
                    </TableCell>
                    <TableCell className="max-w-md truncate text-xs text-muted-foreground">
                      {e.last_error ?? '—'}
                    </TableCell>
                    <TableCell className="text-right text-xs">{e.duration_ms ?? '—'}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {new Date(e.received_at).toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function ConfigRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-xs' : 'text-sm'}>{value}</dd>
    </div>
  );
}

function StatCard({
  icon: Icon,
  tone,
  label,
  stats,
}: {
  icon: typeof CheckCircle2;
  tone: 'success' | 'warning' | 'destructive' | 'muted';
  label: string;
  stats: { '24h': number; '7d': number };
}): JSX.Element {
  const toneClass = {
    success: 'text-emerald-600 dark:text-emerald-400',
    warning: 'text-amber-600 dark:text-amber-400',
    destructive: 'text-destructive',
    muted: 'text-muted-foreground',
  }[tone];
  return (
    <Card>
      <CardContent className="pt-6">
        <div className={`mb-2 flex items-center gap-2 text-xs uppercase tracking-wide ${toneClass}`}>
          <Icon className="h-4 w-4" />
          {label}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="text-2xl font-bold tabular-nums">{stats['24h']}</div>
          <div className="text-xs text-muted-foreground">за 24h</div>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          {stats['7d']} <span className="text-muted-foreground/70">за 7 дней</span>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({
  status,
}: {
  status: 'pending' | 'processing' | 'done' | 'skipped' | 'failed' | 'dead';
}): JSX.Element {
  switch (status) {
    case 'done':
      return <Badge variant="success">done</Badge>;
    case 'skipped':
      return <Badge variant="secondary">skipped</Badge>;
    case 'failed':
      return <Badge variant="destructive">failed</Badge>;
    case 'dead':
      return <Badge variant="destructive">dead</Badge>;
    case 'processing':
      return <Badge variant="warning">processing</Badge>;
    case 'pending':
      return <Badge variant="secondary">pending</Badge>;
  }
}
